/**
 * EmailDrafter — Email Helper tool page
 * (Phase X.3.4 → X.3.4.5 structured-output + Save Draft refactor).
 *
 * X.3.4.5 changes vs X.3.4:
 *   - State carries the full structured EmailDraftResponse (not a plain
 *     text string) so the three-block output renders.
 *   - Adds a Save Draft button below the output. Wires to
 *     aiToolsApi.saveEmailDraft(slug, ...). On success shows a "Saved"
 *     confirmation + a link to the drafts inbox.
 *   - 10-cap (DraftCapExceededError → 409) handled with a specific
 *     error message.
 *
 * Capability gate retained from X.3.1.
 */

import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canUseAiTools, canReadEveryClient, canAuthorClinicalNotes, canViewAnyClientRoster } from '@/lib/capabilities';
import { isSpiceOnlyViewer } from '@/lib/spiceOnlyViewer';
import { ToolPageShell, ToolDraftingState } from '@/components/aiTools/ToolPageShell';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { EmailDraftForm, type EmailFormSeed, type FormInputSnapshot } from '@/components/aiTools/EmailDraftForm';
import { EmailModeToggle, EmailTaskToggle, type EmailMode, type EmailTask } from '@/components/aiTools/EmailModeToggle';
import { EmailPolishForm } from '@/components/aiTools/EmailPolishForm';
import { EmailPolishChanges } from '@/components/aiTools/EmailPolishChanges';
import { type EmailTone } from '@/components/aiTools/EmailToneSelector';
import { EmailDraftHelp } from '@/components/aiTools/EmailDraftHelp';
import { EmailDraftOutput } from '@/components/aiTools/EmailDraftOutput';
import { RefinePanel } from '@/components/aiTools/RefinePanel';
import { SpiceClientPicker } from '@/components/aiTools/SpiceClientPicker';
import { CaseNoteClientChooser } from '@/components/aiTools/CaseNoteClientChooser';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import { aiToolsApi, type EmailDraftResponse } from '@/lib/api';
import { aiRefineKeyOf, aiSaveKeyOf, errorText, type KeyedError } from '@/lib/errorCopy';
import { ErrorNotice } from '@/components/ErrorNotice';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';
import { peekSpiceVisitPrefill, clearSpiceVisitPrefill } from '@/lib/spiceVisitPrefill';
import { useCmHome } from '@/lib/cmHomeApi';
import { practiceTodayIso } from '@/lib/practiceDate';
import { useSpiceClientsList } from '@/lib/clientsApi';
import { displayNameFromRows } from '@/lib/spiceVisitPicker';
import { AI_TOOLS } from './AiTools';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';

const TOOL = AI_TOOLS.find((t) => t.slug === 'email')!;

/**
 * The page opens on the face that fits the viewer (a SPICE-only therapist
 * lands on the SPICE face, with no way to leave it) and reads the viewer
 * from whoami. whoami arrives a moment after the shell
 * renders, so the page waits for it rather than setting itself up on an
 * empty answer. A whoami that failed (the shell records the error) is an
 * answer: the page renders and shows its no-access tile.
 */
/** What an expired sign-in keeps of this page (src/lib/keptWork.ts). */
interface KeptEmail {
  response: EmailDraftResponse;
  editedBody: string;
  formInput: FormInputSnapshot;
  mode: EmailMode;
  task: EmailTask;
}

export function EmailDrafter(): ReactNode {
  const { whoami, error } = useTenant();
  if (!whoami && !error) {
    return (
      <main className="min-h-screen flex items-center justify-center text-sm text-ink-soft">
        Loading…
      </main>
    );
  }
  return <EmailDrafterPage />;
}

function EmailDrafterPage(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  // Arriving from a client's SPICE door (My week / the client record):
  // the client id travels in the URL; the display name and last plan
  // travel in sessionStorage (spiceVisitPrefill.ts) so no narrative text
  // sits in the query string. Read once, at mount.
  const clientId = searchParams.get('client_id');
  const [spiceVisitPrefill, setSpiceVisitPrefill] = useState(() => {
    if (clientId === null) return null;
    const prefill = peekSpiceVisitPrefill(tenantSlug, clientId);
    clearSpiceVisitPrefill(tenantSlug, clientId);
    return prefill;
  });
  // One "For" line at the top of the form names the client on either
  // face; with none named the page is the plain drafting form. Every pick
  // drops the client id into the URL, replacing this history entry rather
  // than pushing a new one. A pick also hands the form a seed: the last
  // plan the picker had on hand (My week for a therapist on her own
  // caseload; nothing on the all-clients path, which never decrypts a plan
  // line) and the contact first name (null from both of today's sources;
  // see SpiceClientPickerRow). The form fills a field from the seed only
  // while that field is empty, so a pick never replaces anything typed.
  const [pickSeed, setPickSeed] = useState<EmailFormSeed | null>(null);
  const pickCountRef = useRef(0);
  // The picker's own row carries the display name, so the "For <name>"
  // line has it without a second read.
  const [pickedDisplayName, setPickedDisplayName] = useState<string | null>(null);
  // Whether the picker is up: opened by "Choose a client" or "Change" on
  // the For line, closed by a pick, by "Write without a client" / "Keep
  // the client", or by switching face. The form stays mounted (hidden)
  // underneath, so nothing typed is lost either way.
  const [choosingClient, setChoosingClient] = useState(false);
  // A viewer who reads every client (the office width, or a line clinician
  // under the tenant's Everyone on staff setting) picks from the
  // tenant-wide SPICE roster; anyone else from her own SPICE caseload.
  const readsEveryClient = canReadEveryClient(whoami);
  // A viewer whose own roster row is SPICE-only has no Mode toggle: the
  // page is its SPICE face, matching her home page's own my_week gate
  // (cmHomeApi.ts). Everyone else opens on Care Management and keeps the
  // toggle.
  const spiceOnlyViewer = isSpiceOnlyViewer(whoami);
  const handlePickClient = (
    id: string,
    lastPlan: string | null,
    displayName: string,
    contactFirstName: string | null,
  ): void => {
    pickCountRef.current += 1;
    setPickSeed({ id: pickCountRef.current, topic: lastPlan, recipient: contactFirstName });
    setPickedDisplayName(displayName);
    setChoosingClient(false);
    // Clears any door-arrival prefill from an EARLIER client on this same
    // mount (reachable via "Change" then a fresh pick), so that client's
    // name no longer outranks this pick's on the For line.
    setSpiceVisitPrefill(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('client_id', id);
        return next;
      },
      { replace: true },
    );
  };
  // The Care Management face's pick: the same chooser the filing dialog
  // uses, which carries no plan line.
  const handlePickCareClient = (id: string, displayName: string): void => {
    handlePickClient(id, null, displayName, null);
  };
  // Remove: the page goes back to the plain form with no client named.
  // The text already typed stays (the form is never remounted), and the
  // face switch, never hidden by a named client, stays where it is.
  const handleRemoveClient = (): void => {
    setPickedDisplayName(null);
    setSpiceVisitPrefill(null);
    setChoosingClient(false);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('client_id');
        return next;
      },
      { replace: true },
    );
  };
  const todayIso = useMemo(() => practiceTodayIso(), []);
  // A direct URL carrying client_id with neither a door prefill nor a
  // local pick (a bookmark, or a reload after the prefill's one-time
  // read already consumed it) names the client from whichever
  // roster/caseload row this viewer's own picker would show — the same
  // structural-facts-only reads as SpiceClientPicker, never a new
  // decrypting read. Only one of the two ever fetches, same as the
  // picker.
  const needsClientNameLookup =
    clientId !== null && spiceVisitPrefill === null && pickedDisplayName === null;
  const nameLookupHome = useCmHome(tenantSlug, todayIso, {
    enabled: needsClientNameLookup && !readsEveryClient,
  });
  const nameLookupSpiceList = useSpiceClientsList(tenantSlug, {
    enabled: needsClientNameLookup && readsEveryClient,
  });
  const clientDisplayName: string | null =
    spiceVisitPrefill?.client_display_name ??
    pickedDisplayName ??
    (clientId === null
      ? null
      : readsEveryClient
        ? displayNameFromRows(nameLookupSpiceList.clients, clientId)
        : displayNameFromRows(nameLookupHome.home?.clients ?? [], clientId));
  // X.6.4B: mode is lifted here so the header chip + output panel react to
  // SPICE. Arriving with a client id means arriving from a SPICE door, so
  // the mode starts SPICE; the switch still shows, since a named client
  // never fixes the face. The Home hero door arrives with ?mode=spice and
  // no client: SPICE is where it opens. A SPICE-only viewer is on the
  // SPICE face whatever was asked, since she has no switch.
  const [pickedMode, setMode] = useState<EmailMode>(
    clientId !== null || searchParams.get('mode') === 'spice' ? 'spice' : 'care_management',
  );
  const mode: EmailMode = spiceOnlyViewer ? 'spice' : pickedMode;
  // Leaving SPICE closes the picker too, so a return to SPICE opens on
  // the form, never on a list left standing.
  const handleModeChange = (next: EmailMode): void => {
    setChoosingClient(false);
    setMode(next);
  };
  // What the page is for right now: drafting an email from notes, or
  // polishing one the writer already wrote (just spelling and grammar).
  // Both faces share the output: the email, its notes, billing on the
  // Care Management face, Save and the filing doors. Only the calls that
  // change the text (draft, refine) belong to drafting.
  const [task, setTask] = useState<EmailTask>('draft');
  // The pass the on-screen email came from: a polished email shows what
  // changed and no refine panel.
  const [lastTask, setLastTask] = useState<EmailTask>('draft');
  // B2: tone register is lifted alongside mode so it threads to the generate
  // call + the saved-draft snapshot. Default Standard = today's voice.
  const [tone, setTone] = useState<EmailTone>('standard');
  const [isGenerating, setIsGenerating] = useState(false);
  const [draftResponse, setDraftResponse] = useState<EmailDraftResponse | null>(null);
  // A2a: the editable email body lives here (seeded from the generation;
  // edited in the workspace textarea). Save + Copy act on this value, so a
  // CM no longer has to save before tweaking the wording.
  const [editedBody, setEditedBody] = useState('');
  const [lastFormInput, setLastFormInput] = useState<FormInputSnapshot | null>(null);
  // The mode the on-screen draft was generated with (may differ from the live
  // toggle if the user flips it after generating); save + output use this.
  const [lastMode, setLastMode] = useState<EmailMode>('care_management');
  // WS-9: failures are stored as registry keys (errorCopy.ts owns the
  // copy); null = no error showing.
  const [saveError, setSaveError] = useState<KeyedError | null>(null);
  const [savedDraftId, setSavedDraftId] = useState<string | null>(null);
  // Second-round edits: the body as it read BEFORE the last refine run, so
  // the operator can get their version back (client-side only; nothing is
  // persisted until Save).
  const [previousBody, setPreviousBody] = useState<string | null>(null);
  // The last refine failure: a registry key, or a refusal for length with
  // the box it names; null = no error showing.
  const [refineFailure, setRefineFailure] = useState<AiToolFailure | null>(null);
  const supportContact = useSupportContact();
  // The email helper's input ceiling (every box on this page), from the
  // tenant config; the factory value until the config has loaded.
  const tenantConfig = useTenantConfig(tenantSlug).data?.config;
  const emailMaxChars = readAiInputMaxChars(tenantConfig, 'email');
  // "Who is this for?" has its own ceiling.
  const recipientMaxChars = readAiInputMaxChars(tenantConfig, 'recipient');
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);

  // Second-round edits: send the CURRENT body (hand edits included) + the
  // original form context + the change request; the server revises rather
  // than regenerates. The response replaces the on-screen draft (new
  // ai_invocation_id rides into the next Save).
  // The request (the body as edited at the press) and the body it
  // replaces travel as the variables.
  const refineMutation = useMutation({
    mutationFn: (v: { request: Parameters<typeof aiToolsApi.refineEmail>[1] | null; bodyBefore: string }) => {
      if (v.request === null) throw new Error('refine called without a generated draft');
      return aiToolsApi.refineEmail(tenantSlug, v.request);
    },
    onSuccess: (res, v) => {
      setPreviousBody(v.bodyBefore);
      setDraftResponse(res);
      setEditedBody(res.email_body);
      setRefineFailure(null);
      setSaveError(null);
      // The on-screen draft changed, so a prior save is stale; Save
      // re-enables and persists the refined text + the new invocation FK.
      setSavedDraftId(null);
    },
    onError: (e: unknown, v) => {
      onCeilingRefusal(e);
      // The refine sends the brief as it was at Generate, so a refusal of
      // the recipient, topic or special instructions is the carried case.
      // A refusal naming no box is about Want changes?.
      setRefineFailure(aiToolFailureOf(e, 'email_refine', 'instruction', v.request, aiRefineKeyOf));
    },
  });

  const handleRefine = (instruction: string): void => {
    refineMutation.mutate({
      request: lastFormInput
        ? {
            current_body: editedBody,
            instruction,
            recipient: lastFormInput.recipient,
            topic: lastFormInput.topic,
            ...(lastFormInput.constraints ? { constraints: lastFormInput.constraints } : {}),
            mode: lastMode,
            tone: lastFormInput.tone,
          }
        : null,
      bodyBefore: editedBody,
    });
  };

  const handleRestorePrevious = (): void => {
    if (previousBody === null) return;
    setEditedBody(previousBody);
    setPreviousBody(null);
    setSavedDraftId(null);
  };

  /** The save request, built from the screen at the press (the body as
   *  edited); null with no draft yet. */
  function saveRequestOf(): Parameters<typeof aiToolsApi.saveEmailDraft>[1] | null {
    if (!draftResponse || !lastFormInput) return null;
    return {
        ai_invocation_id: draftResponse.ai_invocation_id,
        // A2a: persist the EDITED body, not the original generation.
        email_body: editedBody,
        annotations: draftResponse.annotations,
        // X.3.4.10: SPICE drafts carry no billing; omit the key rather than
        // send undefined. The mode marker rides along so the saved draft
        // hides billing when reopened.
        ...(draftResponse.billing_suggestion
          ? { billing_suggestion: draftResponse.billing_suggestion }
          : {}),
        // The SPICE face's medical flag rides with the draft so it shows
        // again above the email on reopen.
        ...(lastMode === 'spice' && draftResponse.medical_flag
          ? { medical_flag: draftResponse.medical_flag }
          : {}),
        mode: lastMode,
        form_input: lastFormInput,
    };
  }

  // The request travels as the variables.
  const saveMutation = useMutation({
    mutationFn: (request: Parameters<typeof aiToolsApi.saveEmailDraft>[1] | null) => {
      if (request === null) throw new Error('saveDraft called without draftResponse');
      return aiToolsApi.saveEmailDraft(tenantSlug, request);
    },
    onSuccess: (res) => {
      setSaveError(null);
      setSavedDraftId(res.id);
    },
    onError: (e: unknown) => {
      setSavedDraftId(null);
      setSaveError(aiSaveKeyOf(e));
    },
  });

  // An expired sign-in keeps the email as edited, with what it was made
  // from (src/lib/keptWork.ts). Before a generation the form keeps its own.
  const kept = useKeptWork<KeptEmail>(
    keptWorkKey(tenantSlug, 'email', null),
    () =>
      draftResponse === null || lastFormInput === null || savedDraftId !== null
        ? null
        : { response: draftResponse, editedBody, formInput: lastFormInput, mode: lastMode, task: lastTask },
    (k) => {
      setDraftResponse(k.response);
      setEditedBody(k.editedBody);
      setLastFormInput(k.formInput);
      setLastMode(k.mode);
      setMode(k.mode);
      setLastTask(k.task);
      setTask(k.task);
      setSavedDraftId(null);
    },
  );
  // Saved: the kept copy has done its job.
  useEffect(() => {
    if (savedDraftId !== null) kept.clear();
  }, [savedDraftId]);

  if (!canUseAiTools(whoami)) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-6">
        <EmptyAiToolsTile />
      </main>
    );
  }

  // Reset save-state whenever a new draft is generated (so a save success
  // banner from the previous generation doesn't linger over a new draft).
  const handleDraftReady = (
    response: EmailDraftResponse,
    formInput: FormInputSnapshot,
    mode: EmailMode,
  ): void => {
    setDraftResponse(response);
    // A2a: seed the editable body from the fresh generation.
    setEditedBody(response.email_body);
    setLastFormInput(formInput);
    setLastMode(mode);
    setLastTask('draft');
    setSaveError(null);
    setSavedDraftId(null);
    // A fresh generation resets the refine trail.
    setPreviousBody(null);
    setRefineFailure(null);
  };

  // A polished email lands the same way a drafted one does. Its form
  // context for Save is the recipient read off the greeting (or "Not
  // named") and the email as pasted, so the draft reopens with what it
  // was made from.
  const handlePolished = (response: EmailDraftResponse, text: string, mode: EmailMode): void => {
    setDraftResponse(response);
    setEditedBody(response.email_body);
    setLastFormInput({
      recipient: response.recipient?.trim() ? response.recipient.trim() : 'Not named',
      topic: text,
      tone: 'standard',
    });
    setLastMode(mode);
    setLastTask('polish');
    setSaveError(null);
    setSavedDraftId(null);
    setPreviousBody(null);
    setRefineFailure(null);
  };

  // The refine notice, built at render: a refusal for length says the
  // server's number, else the named box's ceiling as the config reads now.
  const refineCopy =
    refineFailure === null
      ? null
      : aiToolFailureCopy(refineFailure, (entry) => readAiInputMaxChars(tenantConfig, entry));
  const refineErrorText =
    refineCopy === null ? null : errorText('email_refine', refineCopy.key, supportContact, refineCopy.vars);

  const outputContent: ReactNode | undefined =
    draftResponse !== null ? (
      <div className="space-y-4">
        {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
        <EmailDraftOutput
          response={draftResponse}
          mode={lastMode}
          body={editedBody}
          onBodyChange={setEditedBody}
          clientId={clientId}
          actions={
            <Button
              type="button"
              onClick={() => saveMutation.mutate(saveRequestOf())}
              disabled={saveMutation.isPending || savedDraftId !== null}
              variant="ghost"
              size="sm"
              aria-label="Save to drafts"
              data-testid="email-save-row"
            >
              {savedDraftId !== null
                ? '✓ Saved'
                : saveMutation.isPending
                  ? 'Saving…'
                  : 'Save to drafts'}
            </Button>
          }
        />
        {savedDraftId !== null && (
          <Link
            to={`/t/${tenantSlug}/ai-tools/drafts`}
            className="text-sm text-sage-700 underline hover:text-sage-900"
            data-testid="email-saved-link"
          >
            View drafts inbox
          </Link>
        )}
        {saveError !== null && (
          <ErrorNotice
            surface="email_draft_save"
            code={saveError.key}
            vars={saveError.vars}
            data-testid="email-save-error"
          />
        )}
        {/* A polished email says what was touched; a drafted one offers
            second-round edits (say what to change and run it again). */}
        {lastTask === 'polish' ? (
          <EmailPolishChanges changes={draftResponse.changes ?? []} />
        ) : (
          <RefinePanel
            onRefine={handleRefine}
            pending={refineMutation.isPending}
            error={refineErrorText}
            onRestore={previousBody !== null ? handleRestorePrevious : undefined}
            spice={lastMode === 'spice'}
            maxChars={emailMaxChars}
            testIdPrefix="email-refine"
          />
        )}
      </div>
    ) : isGenerating ? (
      <ToolDraftingState spice={mode === 'spice'} />
    ) : undefined;

  // Either face with no client is the drafting form: nothing to choose
  // before writing. A client arrives in the URL from a door (My week, the
  // client record) or through the For line's picker here; the filing
  // door then files straight to that client.
  const showPicker = choosingClient;

  // One switch, the same shape as Mode, on every face.
  const taskChoice = (
    <div className="mb-4" data-testid="email-task-choice">
      <EmailTaskToggle
        task={task}
        onChange={(next) => {
          if (next === 'polish') setChoosingClient(false);
          setTask(next);
        }}
        spice={mode === 'spice'}
        disabled={isGenerating}
      />
    </div>
  );

  // The For line: the one way to name the client on this page. A seat
  // that cannot author a note has nothing to file, so it is offered no
  // choice, though a client that arrived by URL is still named.
  const canNameClient = canAuthorClinicalNotes(whoami);
  const forLine = (clientId !== null || canNameClient) && (
    <div
      className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-ink-soft"
      data-testid="email-for-line"
    >
      {/* The spaces between items are for anyone reading the line as
          text; the flex gap does the spacing on screen. Every choice
          here is a real button, so none of it reads as running text. */}
      {clientId === null ? (
        <>
          <span>For:</span>{' '}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setChoosingClient(true)}
            data-testid="email-for-choose"
          >
            Choose a client
          </Button>
        </>
      ) : (
        <>
          <span>
            For <span className="font-semibold text-ink">{clientDisplayName ?? 'this client'}</span>
          </span>{' '}
          {canNameClient && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setChoosingClient(true)}
              data-testid="email-for-change"
            >
              Change
            </Button>
          )}{' '}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemoveClient}
            data-testid="email-for-remove"
          >
            Remove
          </Button>
        </>
      )}
    </div>
  );

  return (
    <ToolPageShell
      tool={TOOL}
      tenantSlug={tenantSlug}
      spice={mode === 'spice'}
      inputTitle={task === 'polish' ? 'Your email' : 'Your notes'}
      outputTitle={task === 'polish' ? 'Polished' : 'Draft'}
      howThisWorks={<EmailDraftHelp />}
      inputPlaceholder={
        <>
          {showPicker && (
            <>
              {/* The toggle lives here while the picker is up, since the
                  form (its usual home) has stepped aside. A SPICE-only
                  viewer has no toggle anywhere on this page. */}
              {!spiceOnlyViewer && (
                <div className="mb-4">
                  <EmailModeToggle mode={mode} onChange={handleModeChange} />
                </div>
              )}
              {/* The picker that fits the face: SPICE keeps its own (and the
                  plan from My week); Care Management uses the filing
                  dialog's chooser. */}
              {mode === 'spice' ? (
                <SpiceClientPicker
                  tenantSlug={tenantSlug}
                  rosterOpen={canViewAnyClientRoster(whoami)}
                  ownCaseload={!readsEveryClient}
                  onPick={handlePickClient}
                />
              ) : (
                <CaseNoteClientChooser
                  onChoose={handlePickCareClient}
                  testIdPrefix="email-for"
                  label="Which client is this email about?"
                  labelVisible
                  retryHint="Go back and choose a client again."
                />
              )}
              <div className="mt-4 text-sm text-ink-soft">
                <TextAction onClick={() => setChoosingClient(false)} data-testid="email-for-back">
                  {clientId === null ? 'Write without a client' : 'Keep the client'}
                </TextAction>
              </div>
            </>
          )}
          {/* The form stays mounted while the picker is up, only hidden,
              so a pick or a backed-out Change never loses what was typed.
              Its own face switch steps out meanwhile: the one above the
              picker is the only switch on the page. */}
          <div hidden={showPicker} data-testid="email-form-area">
            {forLine}
            {taskChoice}
            {task === 'polish' ? (
              <EmailPolishForm
                tenantSlug={tenantSlug}
                mode={mode}
                onModeChange={handleModeChange}
                modeLocked={spiceOnlyViewer || showPicker}
                onGeneratingChange={setIsGenerating}
                onPolished={handlePolished}
                maxChars={emailMaxChars}
                hasDraft={draftResponse !== null}
              />
            ) : (
              <EmailDraftForm
                tenantSlug={tenantSlug}
                mode={mode}
                onModeChange={handleModeChange}
                tone={tone}
                onToneChange={setTone}
                onGeneratingChange={setIsGenerating}
                onDraftReady={handleDraftReady}
                hasDraft={draftResponse !== null}
                modeLocked={spiceOnlyViewer || showPicker}
                initialTopic={spiceVisitPrefill?.last_plan ?? ''}
                initialRecipient={spiceVisitPrefill?.contact_first_name ?? ''}
                seed={pickSeed}
                maxChars={emailMaxChars}
                recipientMaxChars={recipientMaxChars}
              />
            )}
          </div>
        </>
      }
      outputContent={showPicker ? undefined : outputContent}
    />
  );
}
